import {ApDynformsConfigFieldset}                 from '../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsConfigComboBox}                 from '../../../ap-dynforms/config/ap-dynforms-config-combobox';
import {ApDynGridColumnConfigBase}                from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {ApDynGridPropertyColumnConfig}            from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {ApDynformsConfigNumerictextbox}           from '../../../ap-dynforms/config/ap-dynforms-config-numerictextbox';
import {delay, distinctUntilChanged, filter, map} from 'rxjs/operators';
import {ApDynformsValidator}                      from '../../../ap-dynforms/ap-dynforms-validator';
import {Validators}                               from '@angular/forms';
import {AgriportConstantsService}                 from '../../../services/common/agriport-constants.service';
import {Observable}                               from 'rxjs';
import {Injectable, TemplateRef}                  from '@angular/core';
import {ApDynGridGroupColumnConfig}               from '../../../ap-dyngrids/config/ap-dyn-grid-group-column-config';
import {GetRoundNumericPipe}                      from '../../../ap-utils';
import {AgriportConstantsEnum}                    from '../../../ap-interface/enums/ap-agriport-constants.enum';

@Injectable()
export class TargetRateConfig {
  private _minAllowed = 0;
  private _maxAllowed = 250;

  constructor(private roundNumericPipe: GetRoundNumericPipe,
              private agriportConstantsService: AgriportConstantsService) {
  }

  public formBuilder(values: any, formValues$: Observable<any>, isRape: boolean, isModalField: boolean, diagramTemplateRef?: TemplateRef<any>): ApDynformsConfigFieldset[] {
    return [
      new ApDynformsConfigFieldset({
        key: 'Agronomy',
        legend: 'Global__Agronomy',
        before: diagramTemplateRef,
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'ECStage',
            label: 'Global__ECStage',
            value: values['ECStage'],
            decimals: 0,
            format: 'n0',
            step: 1,
            min: +this.agriportConstantsService.GetConstant(AgriportConstantsEnum.EcMin),
            max: +this.agriportConstantsService.GetConstant(AgriportConstantsEnum.EcMax),
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigComboBox({
            key: 'ControlFactor',
            label: 'Fertilisation__ControlCurve',
            value: values['ControlFactor'],
            value$: formValues$.pipe(
              map((v) => v['ECStage']),
              filter((v) => isRape || !!v),
              distinctUntilChanged((prev, next) => prev === next),
              map((ecStage) => {
                if (isRape) {
                  return 1;
                } else if (ecStage < +this.agriportConstantsService.GetConstant('EC_QUALITY_MIN')) {
                  return 1;
                } else if (ecStage > +this.agriportConstantsService.GetConstant('EC_YIELD_MAX')) {
                  return -1;
                }
                return undefined;
              }),
              filter((v) => isRape || v != null),
              delay(0)
            ),
            options: formValues$.pipe(
              map((v) => v['ECStage']),
              filter((v) => isRape || !!v),
              distinctUntilChanged(),
              map((ecStage) => {
                const result = [];
                if (isRape) {
                  result.push({value: 1, text: 'Global__Yield_Oriented'});
                } else {
                  if (ecStage >= +this.agriportConstantsService.GetConstant('EC_QUALITY_MIN')) {
                    result.push({value: -1, text: 'Global__Quality_Oriented'});
                  }
                  if (ecStage <= +this.agriportConstantsService.GetConstant('EC_YIELD_MAX')) {
                    result.push({value: 1, text: 'Global__Yield_Oriented'});
                  }
                }
                return result;
              }),
            ),
            textField: 'text',
            valueField: 'value',
            translate: true,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Threshold',
            label: 'Global__Threshold_Percent',
            value: values['Threshold'] ?? 20,
            format: 'n0',
            min: 0,
            max: 100,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          })
        ]
      }),
      new ApDynformsConfigFieldset({
        legend: 'Nutrients_Pages_Popups__Amount',
        key: 'Amount',
        useMaxWidth: isModalField,
        config: [
          new ApDynformsConfigNumerictextbox({
            key: 'TargetRate',
            label: 'Fertilisation__TargetRate_Nutrient_kg_N_per_ha',
            value: values['TargetRate'],
            min: formValues$.pipe(
              map((v) => v['ControlRange']),
              distinctUntilChanged((prev, next) => prev === next),
              map((range) => range ? Math.max(0, range) : 0),
              delay(0)
            ),
            max: this._maxAllowed,
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'ControlRange',
            label: 'Global__Control_Range_kg_N_per_ha',
            value: values['ControlRange'],
            min: this._minAllowed,
            max: formValues$.pipe(
              filter(v => v['TargetRate'] && v['TargetRate'] >= 0),
              map(v => v['TargetRate'])
            ),
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Minimum',
            label: 'Nutrients__Minimum_kg_n_per_ha',
            value: values['Minimum'],
            disabled: true,
            min: this._minAllowed,
            step: 1,
            format: 'n0',
            value$: formValues$.pipe(
              filter((v) => !isNaN(v['TargetRate']) && !isNaN(v['ControlRange'])),
              map((v) => {
                return +v['TargetRate'] - +v['ControlRange'];
              })
            )
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Maximum',
            label: 'Nutrients__Maximum_kg_n_per_ha',
            value: values['Maximum'],
            disabled: true,
            min: this._minAllowed,
            step: 1,
            format: 'n0',
            value$: formValues$.pipe(
              filter((v) => !isNaN(v['TargetRate']) && !isNaN(v['ControlRange'])),
              map((v) => +v['TargetRate'] + +v['ControlRange'])
            )
          }),
          new ApDynformsConfigNumerictextbox({
            key: 'Constant',
            label: 'Nutrients__Constant_Amount_kg_N_per_ha',
            value: values['TargetRate'],
            value$: formValues$.pipe(map(v => v['TargetRate'])),
            disabled: true,
            min: formValues$.pipe(
              filter(x => x['Minimum'] != null && x['Minimum'] >= 0),
              map(v => v['Minimum'])
            ),
            max: formValues$.pipe(
              filter(x => x['Maximum'] != null && x['Maximum'] >= 0),
              map(v => v['Maximum'])
            ),
            step: 1,
            format: 'n0',
            validators: [
              new ApDynformsValidator({validator: Validators.required, errorKey: 'Settings__Msg_Vali_Value_Required'}),
            ]
          })
        ]
      })
    ];
  }

  public gridBuilderConfig(): ApDynGridColumnConfigBase[] {
    return [
      new ApDynGridPropertyColumnConfig({
        title: 'Global__Control_Function',
        field: 'ControlFactorDescription',
        hide: true,
        width: 90
      }),
      new ApDynGridGroupColumnConfig({
        title: 'Global__KgNPerHa',
        groupColumns: [
          new ApDynGridPropertyColumnConfig({
            title: 'AgriConnect_View__TargetRate',
            field: 'TargetRate',
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Threshold',
            field: 'Threshold',
            hide: true,
            width: 50
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__RateConst',
            field: 'Constant',
            hide: true,
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Min',
            field: 'Minimum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          }),
          new ApDynGridPropertyColumnConfig({
            title: 'Global__Max',
            field: 'Maximum',
            width: 50,
            pipes: [{
              pipe: this.roundNumericPipe,
              args: [0]
            }]
          })
        ]
      })
    ];
  }
}
